import {
  BasePageModel,
  basePageToLocal,
  FrameType,
  TrainingPhraseConfig,
  trainingPhraseToLocal,
  trainingPhraseToServer,
  ParameterType,
  ReferenceNode,
  referenceNodeToLocal,
  referenceNodeToServer,
  KeyValuePair,
  ParameterConfig,
  AugmentMeta,
  augmentMetaToLocal,
  augmentMetaToServer
} from '@interface/index';
import { localizedString } from '@locale/index';
import { AugmentMetaComp } from '@page/agent/component/augment-meta';

export interface DataQueryUpdateFuncArgumentConfig {
  nextDataQueries?: DataQuery[];
  nextDelDataQueries?: DataQuery[];
  nextDelDataQuery?: DataQuery;
  nextRevertDataQueryId?: string;
}

export enum ProviderType {
  Postgrest = 'database',
  Restful = 'restful',
  Native = 'function',
  GoogleSheets = 'google_sheets',
  Supabase = 'supabase',
  Unknown = ''
}

export function dataQueryTypeString(value: ProviderType) {
  switch (value) {
    case ProviderType.Postgrest:
      return localizedString('data_query_mode_database');
    case ProviderType.Restful:
      return localizedString('data_query_mode_restful');
    case ProviderType.Native:
      return localizedString('data_query_mode_native');
    case ProviderType.GoogleSheets:
      return localizedString('data_query_mode_google_sheets');
    case ProviderType.Supabase:
      return localizedString('data_query_mode_supabase');
    default:
      return '';
  }
}

export type FunctionParameter = {
  name: string,
  type?: ReferenceNode;
  multiValue: boolean;
  nullable: boolean;
  comment?: string;
};

export enum InputParameterType {
  Variable = 'variable',
  Constant = 'constant'
}


export type InputParameter = {
  type: InputParameterType;
  variable: ReferenceNode[];
  constant: string;
};

export function inputParameterToLocal(res: any): InputParameter {
  return {
    type: res.type,
    variable: res.variable.map(referenceNodeToLocal),
    constant: res.constant
  };
}

export function inputParameterToServer(data: InputParameter): any {
  return {
    type: data.type,
    variable: data.variable.map(referenceNodeToServer),
    constant: data.constant
  };
}


export interface DataQuery extends BasePageModel {
  queryType?: ProviderType;
  schedule?: boolean;
  kotlin: boolean;
  parameters: FunctionParameter[];
  frame?: ReferenceNode;
  databaseConfig?: {
    query: string;
    functionMeta: KeyValuePair[];
    cron?: string;
  };
  restfulConfig?: {
    method: string;
    url: string;
    path: string;
    postBody?: string;
    headers?: KeyValuePair[];
  };
  functionConfig?: {
    isStatic: boolean;
    body: string;
  };
  promptImplemented?: boolean;
  promptConfig?: AugmentMeta;
  bodyComment?: string;
  multiValue: boolean;
  disableReturnNull: boolean;
  errorMsgs?: string[];
  allowTrigger: boolean;
  triggerExpressions?: TrainingPhraseConfig[];
  dirty?: boolean;
  readonly?: boolean;
  masterSelf?: DataQuery;
}

export function dataQueryToLocal(data: any): DataQuery {
  return {
    ...basePageToLocal(data),
    readonly: data.readonly,
    parameters: data.parameters &&
      data.parameters.map((item: any) => ({
        name: item.name,
        type: {
          id: item.type.id,
          name: item.type.name,
          type: item.type.type
        },
        multiValue: item.multi_value,
        nullable: item.nullable,
        comment: item.comment
      })) || [],
    queryType: data.query_type,
    schedule: data.schedule,
    kotlin: data.kotlin,
    databaseConfig: data.database_config && {
      query: data.database_config.query,
      functionMeta: data.database_config.function_meta,
      cron: data.database_config.cron
    },
    restfulConfig: data.restful_config && {
      method: data.restful_config.method,
      url: data.restful_config.url,
      path: data.restful_config.path,
      postBody: data.restful_config.post_body,
      headers: data.restful_config.headers &&
        data.restful_config.headers.map((item: any) => ({
          key: item.key,
          value: item.value
        }))
    },
    functionConfig: data.function_config && {
      isStatic: data.function_config.is_static,
      body: data.function_config.body
    },
    promptImplemented: data.prompt_implemented,
    promptConfig: data.prompt_config && augmentMetaToLocal(data.prompt_config),
    bodyComment: data.body_comment,
    errorMsgs: data.error_messages,
    frame: data.type && {
      id: data.type.id,
      type: data.type.type,
      name: data.type.name
    },
    multiValue: data.multi_value,
    disableReturnNull: data.disable_return_null,
    allowTrigger: data.allow_trigger,
    triggerExpressions: data.trigger_expressions &&
      data.trigger_expressions.map(trainingPhraseToLocal),
    masterSelf: data.master_self && dataQueryToLocal(data.master_self)
  };
}

export function dataQueryToServer(data: DataQuery) {
  return {
    id: data.id,
    name: data.name,
    comment: data.comment,
    parameters: data.parameters &&
      data.parameters.map((item: FunctionParameter) => ({
        name: item.name,
        type: item.type && {
          id: item.type.id,
          name: item.type.name,
          type: item.type.type
        },
        multi_value: item.multiValue,
        nullable: item.nullable,
        comment: item.comment
      })) || [],
    query_type: data.queryType || null,
    schedule: data.schedule,
    kotlin: data.kotlin,
    database_config: data.databaseConfig && {
      query: data.databaseConfig.query,
      function_meta: data.databaseConfig.functionMeta,
      cron: data.databaseConfig.cron
    },
    restful_config: data.restfulConfig && {
      method: data.restfulConfig.method,
      url: data.restfulConfig.url,
      path: data.restfulConfig.path,
      post_body: data.restfulConfig.postBody,
      headers: data.restfulConfig.headers &&
        data.restfulConfig.headers.map((item: KeyValuePair) => ({
          key: item.key,
          value: item.value
        }))
    },
    function_config: data.functionConfig && {
      is_static: data.functionConfig.isStatic,
      body: data.functionConfig.body
    },
    prompt_implemented: data.promptImplemented,
    prompt_config: data.promptConfig && augmentMetaToServer(data.promptConfig),
    body_comment: data.bodyComment,
    error_messages: data.errorMsgs,
    state: data.state,
    type: data.frame && {
      id: data.frame.id,
      type: data.frame.type,
      name: data.frame.name
    },
    multi_value: data.multiValue,
    disable_return_null: data.disableReturnNull,
    allow_trigger: data.allowTrigger,
    trigger_expressions: data.triggerExpressions &&
      data.triggerExpressions.map(trainingPhraseToServer)
  };
}
